/**
 * @file PermissionPicker.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-27
 * @modified 2026-08-27
 *
 * The category-grouped permission list, in the two modes it is needed in.
 *
 *   mode="binary"    Editing a ROLE. Each permission is simply in or out.
 *   mode="tristate"  Editing a USER. Each permission is inherited from their
 *                    roles, or explicitly added, or explicitly removed.
 *
 * The tri-state is presented as a plain checkbox rather than as three radio
 * buttons, because with ninety permissions a segmented control per row is
 * unreadable. Clicking flips the effective value and marks the row as an
 * exception; clicking back lands on "inherited" again rather than on the
 * opposite exception. So the three states are reachable with the one control
 * everyone already understands, and the two that are exceptions say so.
 *
 * Both modes render every permission in the same order and grouping as
 * config/permissionRegistry.js, so the picker reads like the nav.
 */

import React, { useMemo, useState } from 'react';
import TickCheckbox from './TickCheckbox';
import './PermissionPicker.css';

/** The three states a permission can be in for a user. */
export const INHERIT = 'inherit';
export const GRANT = 'grant';
export const REVOKE = 'revoke';

/**
 * Turn an override map ({ key: true|false }) into per-key state, given what the
 * user's roles already grant.
 */
export function stateFor(key, overrides, roleGrants) {
  if (overrides[key] === true) return roleGrants.has(key) ? INHERIT : GRANT;
  if (overrides[key] === false) return roleGrants.has(key) ? REVOKE : INHERIT;
  return INHERIT;
}

/** Whether a permission is effectively held, given its state. */
export function effectiveFor(key, state, roleGrants) {
  if (state === GRANT) return true;
  if (state === REVOKE) return false;
  return roleGrants.has(key);
}

function PermissionPicker({
  categories = [],
  permissions = [],
  mode = 'binary',
  // binary mode
  selected = new Set(),
  onToggle,
  onSetMany,
  // tristate mode
  overrides = {},
  roleGrants = new Set(),
  roleNameFor,
  onOverrideChange,
  // both
  disabled = false,
  emptyMessage = 'No permissions defined.'
}) {
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState(() => new Set());

  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const byCategory = new Map(categories.map(c => [c.key, { ...c, items: [] }]));
    // A permission whose category has been retired from the registry still has
    // to render somewhere rather than vanishing from the editor.
    const orphans = { key: '_other', label: 'Other', sort_order: 999, items: [] };

    for (const p of permissions) {
      if (term && !`${p.label} ${p.key} ${p.description || ''}`.toLowerCase().includes(term)) continue;
      (byCategory.get(p.category) || orphans).items.push(p);
    }
    const out = [...byCategory.values()].filter(c => c.items.length);
    if (orphans.items.length) out.push(orphans);
    return out.sort((a, b) => a.sort_order - b.sort_order);
  }, [categories, permissions, search]);

  const toggleCategory = (key) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  if (!permissions.length) {
    return <p className="no-permissions">{emptyMessage}</p>;
  }

  return (
    <div className="permission-picker">
      <div className="permission-search">
        <span className="material-symbols-rounded">search</span>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter permissions…"
          aria-label="Filter permissions"
        />
        {search && (
          <button className="btn-icon" onClick={() => setSearch('')} title="Clear filter">
            <span className="material-symbols-rounded">close</span>
          </button>
        )}
      </div>

      {grouped.length === 0 && (
        <p className="no-permissions">Nothing matches “{search}”.</p>
      )}

      {grouped.map(category => {
        const isCollapsed = collapsed.has(category.key);
        const keys = category.items.map(i => i.key);
        const allOn = mode === 'binary'
          ? keys.every(k => selected.has(k))
          : keys.every(k => effectiveFor(k, stateFor(k, overrides, roleGrants), roleGrants));
        const someOn = mode === 'binary'
          ? keys.some(k => selected.has(k))
          : keys.some(k => effectiveFor(k, stateFor(k, overrides, roleGrants), roleGrants));

        return (
          <div className={`permission-category${isCollapsed ? ' collapsed' : ''}`} key={category.key}>
            <div className="permission-category-header">
              <button
                type="button"
                className="permission-category-toggle"
                onClick={() => toggleCategory(category.key)}
                aria-expanded={!isCollapsed}
              >
                <span className="material-symbols-rounded">
                  {isCollapsed ? 'chevron_right' : 'expand_more'}
                </span>
                {category.label}
              </button>
              <span className="permission-category-count">
                {someOn ? `${keys.filter(k => (mode === 'binary'
                  ? selected.has(k)
                  : effectiveFor(k, stateFor(k, overrides, roleGrants), roleGrants))).length} / ${keys.length}` : `0 / ${keys.length}`}
              </span>
              {mode === 'binary' && (
                <button
                  type="button"
                  className="permission-category-all"
                  disabled={disabled}
                  onClick={() => onSetMany?.(keys, !allOn)}
                >
                  {allOn ? 'Clear all' : 'Select all'}
                </button>
              )}
            </div>

            {!isCollapsed && (
              <div className="permission-list">
                {category.items.map(perm => {
                  const state = mode === 'tristate'
                    ? stateFor(perm.key, overrides, roleGrants)
                    : null;
                  const checked = mode === 'binary'
                    ? selected.has(perm.key)
                    : effectiveFor(perm.key, state, roleGrants);
                  const grantedBy = mode === 'tristate' && roleGrants.has(perm.key)
                    ? roleNameFor?.(perm.key)
                    : null;

                  return (
                    <div
                      className={`permission-item${state && state !== INHERIT ? ` override-${state}` : ''}`}
                      key={perm.key}
                    >
                      <TickCheckbox
                        id={`perm-${perm.key}`}
                        checked={checked}
                        disabled={disabled}
                        ariaLabel={perm.label}
                        onChange={() => {
                          if (mode === 'binary') return onToggle?.(perm.key);
                          // Flip the effective value, expressed as an exception;
                          // flipping back returns to inherited rather than to the
                          // opposite exception.
                          if (state !== INHERIT) return onOverrideChange?.(perm.key, INHERIT);
                          return onOverrideChange?.(
                            perm.key,
                            roleGrants.has(perm.key) ? REVOKE : GRANT
                          );
                        }}
                      />
                      <label htmlFor={`perm-${perm.key}`} className="permission-info">
                        <div className="permission-label">
                          {perm.label}
                          {state === GRANT && <span className="permission-chip added">Added for this user</span>}
                          {state === REVOKE && <span className="permission-chip removed">Removed for this user</span>}
                          {state === INHERIT && grantedBy && (
                            <span className="permission-chip inherited">from {grantedBy}</span>
                          )}
                        </div>
                        {perm.description && (
                          <div className="permission-description">{perm.description}</div>
                        )}
                      </label>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default PermissionPicker;
